/**
 * @module components/layout/SidebarNav
 * @description Enterprise navigation sidebar supporting both Super Admin (`/s-admin/*`)
 * and Client Portal (`/client/*`) workspaces with white-label `theme_color` accent pills.
 */

import * as React from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Users,
  Building2,
  Cpu,
  MonitorSmartphone,
  Tv,
  UserCheck,
  LogOut,
  Moon,
  Sun,
  Laptop,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { APP_NAME } from "@/config/env";
import type { ClientBrandingDto } from "@/types/auth";

interface NavItem {
  label: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  badge?: string | number;
}

interface NavSection {
  title: string;
  items: NavItem[];
}

const superAdminNavSections: NavSection[] = [
  {
    title: "OPERATIONS",
    items: [
      { label: "User Directory", href: "/s-admin/users", icon: Users },
      { label: "Client Management", href: "/s-admin/clients", icon: Building2 },
      {
        label: "Client Devices",
        href: "/s-admin/client-devices",
        icon: MonitorSmartphone,
      },
      {
        label: "Display Module",
        href: "/s-admin/display-module",
        icon: Tv,
      },
      { label: "Devices", href: "/s-admin/devices", icon: Cpu },
      { label: "My Profile", href: "/s-admin/profile", icon: UserCheck },
    ],
  },
];

const clientPortalNavSections: NavSection[] = [
  {
    title: "CLIENT WORKSPACE",
    items: [
      {
        label: "Display Module",
        href: "/client/display-module",
        icon: Tv,
      },
    ],
  },
];

interface SidebarNavProps {
  currentUser?: {
    name: string;
    email_id: string;
    role: string;
    avatar_url: string;
    client?: ClientBrandingDto | null;
  } | null;
  onLogout?: () => void;
}

export function SidebarNav({ currentUser, onLogout }: SidebarNavProps) {
  const location = useLocation();
  const isClientPortal =
    currentUser?.role === "client" || location.pathname.startsWith("/client");
  const activeSections = isClientPortal
    ? clientPortalNavSections
    : superAdminNavSections;

  const [theme, setTheme] = React.useState<"light" | "dark" | "system">(() => {
    return (
      (localStorage.getItem("theme") as "light" | "dark" | "system") || "system"
    );
  });

  const toggleTheme = (newTheme: "light" | "dark" | "system") => {
    setTheme(newTheme);
    localStorage.setItem("theme", newTheme);
    const isDark =
      newTheme === "dark" ||
      (newTheme === "system" &&
        window.matchMedia("(prefers-color-scheme: dark)").matches);
    document.documentElement.classList.toggle("dark", isDark);
  };

  const initials = React.useMemo(() => {
    if (!currentUser?.name) return "AD";
    const parts = currentUser.name.trim().split(" ");
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return currentUser.name.slice(0, 2).toUpperCase();
  }, [currentUser?.name]);

  return (
    <aside
      aria-label="Sidebar Navigation"
      className="hidden sm:flex flex-col border border-border/80 dark:border-zinc-800/80 bg-card/95 dark:bg-zinc-950/90 backdrop-blur-xl sm:w-16 lg:w-64 shrink-0 sm:my-3 sm:ml-3 sm:h-[calc(100dvh-1.5rem)] lg:my-4 lg:ml-4 lg:h-[calc(100dvh-2rem)] rounded-2xl lg:rounded-3xl shadow-lg shadow-black/[0.04] dark:shadow-2xl dark:shadow-black/70 ring-1 ring-black/[0.03] dark:ring-white/[0.05] sticky top-3 lg:top-4 transition-all duration-200 z-30 overflow-hidden"
    >
      {/* Brand Header */}
      <div className="flex h-16 items-center px-4 border-b border-border/50 dark:border-zinc-800/80 sm:justify-center lg:justify-between shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <div
            style={
              isClientPortal
                ? { borderColor: "var(--color-brand-primary)" }
                : undefined
            }
            className="h-7 w-7 rounded-full border-[2px] border-foreground flex items-center justify-center shrink-0"
          >
            <div
              style={
                isClientPortal
                  ? { backgroundColor: "var(--color-brand-primary)" }
                  : undefined
              }
              className="h-2 w-2 rounded-full bg-foreground"
            />
          </div>
          <div className="hidden lg:flex flex-col min-w-0">
            <span className="font-bold text-base tracking-tight text-foreground font-sans truncate leading-tight">
              {currentUser?.client?.org_name || `${APP_NAME.toLowerCase()}.`}
            </span>
            {currentUser?.client?.org_name && (
              <span className="text-[10px] text-muted-foreground truncate">
                {APP_NAME} Client Portal
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Nav List with Sections */}
      <div className="flex-1 px-3 py-4 space-y-5 overflow-y-auto min-h-0">
        {activeSections.map((section, sIdx) => (
          <div key={sIdx} className="space-y-1">
            <p className="hidden lg:block px-3 text-[11px] font-semibold text-muted-foreground/70 uppercase tracking-wider mb-1.5">
              {section.title}
            </p>
            <div className="space-y-1">
              {section.items.map((item) => {
                const isActive = item.href.includes("?")
                  ? location.pathname + location.search === item.href
                  : location.pathname.startsWith(item.href);
                const Icon = item.icon;

                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    style={
                      isActive && isClientPortal
                        ? {
                            backgroundColor: "var(--color-brand-primary)",
                            color: "var(--color-brand-foreground)",
                          }
                        : undefined
                    }
                    className={cn(
                      "group flex items-center justify-between rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-150 touch:min-h-[44px]",
                      isActive
                        ? "bg-zinc-950 hover:bg-zinc-900 text-white dark:bg-zinc-100 dark:hover:bg-white dark:text-zinc-950 shadow-sm font-semibold"
                        : "text-muted-foreground hover:bg-muted/70 dark:hover:bg-zinc-900/60 hover:text-foreground",
                      "sm:justify-center lg:justify-between"
                    )}
                    title={item.label}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <Icon
                        className={cn(
                          "h-[18px] w-[18px] shrink-0 transition-colors",
                          isActive
                            ? "text-white dark:text-zinc-950"
                            : "text-muted-foreground group-hover:text-foreground"
                        )}
                      />
                      <span className="hidden lg:inline truncate">
                        {item.label}
                      </span>
                    </div>

                    {item.badge && (
                      <span
                        className={cn(
                          "hidden lg:inline-flex text-[11px] font-semibold px-2 py-0.5 rounded-full shrink-0",
                          isActive
                            ? "bg-white/20 text-white"
                            : "bg-muted text-muted-foreground group-hover:bg-muted/80"
                        )}
                      >
                        {item.badge}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Footer Area: Theme Switcher & User Profile */}
      <div className="p-3 border-t border-border/50 dark:border-zinc-800/80 space-y-3 bg-muted/20 dark:bg-zinc-900/40 backdrop-blur-sm shrink-0">
        {/* Theme Segmented Switcher (Desktop) */}
        <div className="hidden lg:block">
          <div className="flex items-center justify-between mb-1.5 px-1">
            <span className="text-[10px] font-semibold text-muted-foreground/70 uppercase tracking-wider">
              Mode
            </span>
            <span className="text-[10px] text-muted-foreground font-mono capitalize">
              {theme}
            </span>
          </div>
          <div className="flex items-center p-1 rounded-xl bg-muted/60 dark:bg-zinc-950 border border-border/60 dark:border-zinc-800/80">
            <button
              type="button"
              onClick={() => toggleTheme("light")}
              className={cn(
                "flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs font-medium transition-all",
                theme === "light"
                  ? "bg-white dark:bg-zinc-900 text-foreground shadow-xs border border-border/40 font-semibold"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              <Sun className="h-3.5 w-3.5 text-amber-500" />
              <span>Light</span>
            </button>
            <button
              type="button"
              onClick={() => toggleTheme("system")}
              className={cn(
                "flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs font-medium transition-all",
                theme === "system"
                  ? "bg-white dark:bg-zinc-900 text-foreground shadow-xs border border-border/40 font-semibold"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              <Laptop className="h-3.5 w-3.5" />
              <span>Auto</span>
            </button>
            <button
              type="button"
              onClick={() => toggleTheme("dark")}
              className={cn(
                "flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs font-medium transition-all",
                theme === "dark"
                  ? "bg-white dark:bg-zinc-900 text-foreground shadow-xs border border-border/40 font-semibold"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              <Moon className="h-3.5 w-3.5 text-zinc-300" />
              <span>Dark</span>
            </button>
          </div>
        </div>

        {/* Collapsed Theme Switcher (Tablet) */}
        <div className="flex lg:hidden sm:justify-center">
          <button
            type="button"
            onClick={() => {
              const nextTheme =
                theme === "light"
                  ? "dark"
                  : theme === "dark"
                    ? "system"
                    : "light";
              toggleTheme(nextTheme);
            }}
            className="flex h-9 w-9 items-center justify-center rounded-xl border border-border/70 dark:border-zinc-800 bg-muted/40 text-muted-foreground hover:text-foreground"
            title="Toggle Theme"
          >
            {theme === "light" && <Sun className="h-4 w-4 text-amber-500" />}
            {theme === "dark" && <Moon className="h-4 w-4 text-zinc-300" />}
            {theme === "system" && <Laptop className="h-4 w-4" />}
          </button>
        </div>

        {/* User Card with Avatar, Info, and Logout Confirmation Trigger */}
        {currentUser && (
          <div className="flex flex-col lg:flex-row items-center gap-2.5 p-2 rounded-2xl bg-card dark:bg-zinc-950 border border-border/60 dark:border-zinc-800/80 shadow-xs sm:justify-center lg:justify-between">
            <div className="flex items-center gap-2.5 min-w-0">
              <img
                src={currentUser.avatar_url}
                alt={currentUser.name}
                className="h-8 w-8 rounded-full bg-muted object-cover ring-1 ring-border/70 dark:ring-white/10 shrink-0"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).src =
                    `data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 32 32'><rect width='32' height='32' fill='%2310b981'/><text x='16' y='21' font-size='13' font-family='sans-serif' font-weight='600' text-anchor='middle' fill='white'>${initials}</text></svg>`;
                }}
              />
              <div className="hidden lg:flex flex-col min-w-0">
                <span className="text-xs font-semibold text-foreground truncate leading-tight">
                  {currentUser.name}
                </span>
                <span className="text-[11px] text-muted-foreground truncate leading-tight">
                  {currentUser.email_id}
                </span>
              </div>
            </div>

            {onLogout && (
              <button
                type="button"
                onClick={onLogout}
                className="flex h-8 w-8 items-center justify-center rounded-xl text-muted-foreground hover:text-red-500 hover:bg-red-500/10 dark:hover:bg-red-500/20 transition-all shrink-0"
                title="Sign out of account"
              >
                <LogOut className="h-4 w-4" />
              </button>
            )}
          </div>
        )}
      </div>
    </aside>
  );
}
